// The S10 quantity control — STEPPER + NUMPAD (spec 04 §4 row 10, D-091).
//
// §4 row 10 says "qty stepper + numpad" and until 2026-08-08 only the stepper
// half existed: ± was a one-tap INTEGER step, so a measured 49.4 m — the
// NORMAL case for cable lengths, §2.4's quantities are numeric(14,3) — could
// only be entered through the OS keyboard, which a worker in gloves does not
// have. This component is both halves:
//
//  * ±1 stays ONE TAP and stays integer-sized (the glove-fast path for piece
//    counts), exact over a decimal value (`bumpQty` — the fraction rides
//    verbatim, never through a float).
//  * THE NUMPAD is the control's own glove-sized keys — digits, a decimal
//    key, backspace — opened by one tap, so a fractional quantity never
//    depends on the OS keyboard. Calculator entry semantics: the first key
//    replaces, later keys append.
//
// PRECISION IS THE UNIT'S. `precision` arrives from the snapshot's
// `uom_decimal_precision` (the catalogue's per-unit `decimal_places` clamped
// to the field's wire) through `clampPrecision`, which also absorbs a stale
// cached snapshot that predates the field. Precision 0 = an integer unit: no
// decimal key exists and a typed separator is refused.
//
// AND THE WIRE IS THE FIELD'S, NOT ALWAYS §2.4'S (`maxPrecision`, 2026-08-21).
// The typed path admits everything the field's PAYLOAD admits, which for a
// production quantity is 3 dp and for the guided cut's handles is module 06's
// six (`numeric(18,6)`). It was hardcoded to 3, and a six-place handle then
// went DEAD to caret editing: focus selects all so the first keystroke
// replaced everything and worked, but a second tap on a touch screen places a
// caret, backspace yields `1543.50025`, the 3-dp regex refuses it, and the
// field freezes with no sentence anywhere. `maxPrecision` defaults to
// `QTY_MAX_PRECISION`, so every caller that omits it — every one but the cut
// handles — keeps the exact regex and the exact numpad it had.
//
// LOCALE IS DISPLAY ONLY. The input SHOWS the locale's separator (ru: comma)
// and the decimal key is LABELLED with it, but `value` — and therefore every
// payload built from it — is the contract's canonical dot. Typing accepts
// both separators regardless of locale (hardware numeric pads have a dot).
//
// AND `formatDisplay` SEPARATES DISPLAY FROM VALUE ALTOGETHER (2026-08-21).
// The default is unchanged for every caller that passes nothing: the input
// shows the editing value with the locale separator swapped in. A caller whose
// value is a WIRE measure — the guided cut's handles, seeded from module 06's
// `numeric(18,6)` — needs "49.500000" to read as "49.5" at the saw without the
// stored value being rounded, truncated or routed through a float on the way
// there. So the formatter is applied for DISPLAY only, and only while the
// field is NOT being edited: the moment it takes focus it shows the canonical
// editing text again, so every keystroke path is byte-identical to what it was
// before this prop existed. That focus swap is load-bearing rather than
// fussy — `formatMeasure` groups thousands ("1 543.5"), and a grouped string
// typed back into `parseTypedQty` is refused, which would freeze the field on
// exactly the long drums this screen exists for.
//
// THE WEDGE CANNOT EAT A NUMPAD TAP. The D-086 wedge claim in `ScanInput`
// listens to KEYBOARD bursts (sub-35 ms keydown gaps); numpad taps are
// pointer clicks on <button>s and produce no keydown at all, so a screen
// carrying a wedge-claiming scan box cannot mistake numpad entry for a
// scanner — asserted in this component's test, not just stated here.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  bumpQty,
  clampPrecision,
  clampWirePrecision,
  decimalSeparator,
  displayQty,
  numpadBackspace,
  numpadDigit,
  numpadSeparator,
  parseTypedQty,
} from "../lib/qty-entry";

const NUMPAD_DIGITS = ["7", "8", "9", "4", "5", "6", "1", "2", "3"] as const;

export function QtyStepper({
  value,
  onChange,
  testId,
  precision: rawPrecision,
  maxPrecision: rawMaxPrecision,
  locale,
  formatDisplay,
}: {
  /** Canonical-dot editing value (the wire's own shape). */
  value: string;
  onChange: (next: string) => void;
  testId: string;
  /** The unit's entry precision — pass the snapshot's
   * `uom_decimal_precision`; `undefined` (a stale pre-D-091 cache) falls
   * back to this field's wire maximum via `clampPrecision`. */
  precision?: number | undefined;
  /** THE FIELD'S WIRE ceiling — how many fraction digits the payload behind
   * this control can carry. Omit it and it is §2.4's 3 (`ProductionQty`); the
   * guided cut's handles pass `MEASURE_MAX_PRECISION`, because a handle is a
   * module-06 measure at `numeric(18,6)` and a 3-dp regex freezes the field
   * the moment a six-place seed is caret-edited (see the header). It bounds
   * BOTH halves of the control: the typed regex, and `precision` itself. */
  maxPrecision?: number | undefined;
  /** Display locale (ru shows a comma). The wire stays canonical dot. */
  locale: string;
  /** OPTIONAL display formatter over the canonical value, applied only while
   * the field is not focused (see the header). Omit it and this control
   * behaves exactly as it always has. It must never be used to change what is
   * SENT — `value` remains the authority and is passed through untouched. */
  formatDisplay?: ((canonical: string) => string) | undefined;
}) {
  const { t } = useTranslation();
  // The field's wire first; the unit's precision is then clamped INTO it, so
  // a 6-dp field never offers numpad keys the payload cannot carry and a 3-dp
  // field is untouched by the prop existing.
  const wirePrecision = clampWirePrecision(rawMaxPrecision);
  const precision = clampPrecision(rawPrecision, wirePrecision);
  const separator = decimalSeparator(locale);
  const [padOpen, setPadOpen] = useState(false);
  // "Is the operator typing into this field right now" — the one thing that
  // decides whether `formatDisplay` gets to speak. Numpad taps are not focus,
  // and are deliberately shown formatted.
  const [editing, setEditing] = useState(false);
  // Calculator freshness: the first numpad key after opening replaces the
  // value. A ref, not state — flipping it must not re-render.
  const fresh = useRef(true);

  const keyed = (next: string): void => {
    fresh.current = false;
    onChange(next);
  };

  return (
    <div data-testid={`${testId}-control`}>
      <div className="op-qty-row">
        <button
          type="button"
          className="floor-btn"
          aria-label="−1"
          onClick={() => {
            fresh.current = false;
            onChange(bumpQty(value, -1));
          }}
        >
          −
        </button>
        <input
          className="op-qty-input"
          inputMode={precision > 0 ? "decimal" : "numeric"}
          value={
            !editing && formatDisplay !== undefined
              ? formatDisplay(value)
              : displayQty(value, separator)
          }
          data-testid={testId}
          onChange={(event) => {
            // TYPED entry admits everything THIS FIELD'S WIRE admits, not
            // only the unit's precision: quantities are communicated in wire
            // form ("49.400" on a 1-dp metre is the same legal number), and a
            // keystroke the contract accepts must never be refused. The
            // UNIT's precision shapes the NUMPAD keys — the glove path this
            // control exists for — not the keyboard fallback.
            const parsed = parseTypedQty(event.target.value, wirePrecision);
            if (parsed !== undefined) keyed(parsed);
          }}
          onFocus={(event) => {
            setEditing(true);
            event.target.select();
          }}
          onBlur={() => {
            setEditing(false);
          }}
        />
        <button
          type="button"
          className="floor-btn"
          aria-label="+1"
          onClick={() => {
            fresh.current = false;
            onChange(bumpQty(value, 1));
          }}
        >
          +
        </button>
      </div>

      {!padOpen ? (
        <button
          type="button"
          className="floor-btn op-numpad-toggle"
          data-testid={`${testId}-numpad-toggle`}
          onClick={() => {
            fresh.current = true;
            setPadOpen(true);
          }}
        >
          {t("production.floor.op.numpadOpen")}
        </button>
      ) : (
        <div className="op-numpad" data-testid={`${testId}-numpad`}>
          {NUMPAD_DIGITS.map((digit) => (
            <button
              key={digit}
              type="button"
              className="floor-btn op-numpad-key"
              data-testid={`${testId}-numpad-${digit}`}
              onClick={() => {
                keyed(numpadDigit(value, digit, precision, fresh.current));
              }}
            >
              {digit}
            </button>
          ))}
          {precision > 0 ? (
            <button
              type="button"
              className="floor-btn op-numpad-key"
              aria-label={t("production.floor.op.numpadDecimal")}
              data-testid={`${testId}-numpad-sep`}
              onClick={() => {
                keyed(numpadSeparator(value, precision, fresh.current));
              }}
            >
              {separator}
            </button>
          ) : (
            // Integer unit: no decimal key AT ALL — a disabled key would say
            // "later", and there is no later for pieces. The cell stays so 0
            // keeps its calculator position.
            <span aria-hidden="true" />
          )}
          <button
            type="button"
            className="floor-btn op-numpad-key"
            data-testid={`${testId}-numpad-0`}
            onClick={() => {
              keyed(numpadDigit(value, "0", precision, fresh.current));
            }}
          >
            0
          </button>
          <button
            type="button"
            className="floor-btn op-numpad-key"
            aria-label={t("production.floor.op.numpadBackspace")}
            data-testid={`${testId}-numpad-back`}
            onClick={() => {
              keyed(numpadBackspace(value));
            }}
          >
            ⌫
          </button>
          <button
            type="button"
            className="floor-btn floor-btn-primary op-numpad-done"
            data-testid={`${testId}-numpad-done`}
            onClick={() => {
              setPadOpen(false);
            }}
          >
            {t("production.floor.op.numpadDone")}
          </button>
        </div>
      )}
    </div>
  );
}
